import { createRouter, createWebHashHistory } from 'vue-router'
import { getAdminToken } from '@/utils/adminAuth'
import {
  getPreferredLocale,
  pathWithPreferredLocale,
  setActiveRouteLocale,
  setStoredLocale,
  isSupportedLocale
} from '@/utils/locale'
import localeChildRoutes from '@/router/localeRoutes'
import i18n, { setI18nLocale } from '@/i18n'
import { messages } from '@/i18n/messages'
import { getRootRedirectPath } from '@/config/routeDefaults'

function shouldSkipLocaleRedirect (path) {
  return path.startsWith('/admin')
}

function normalizeIncomingPath (path) {
  if (path === '/') return path
  const segs = path.split('/').filter(Boolean)
  if (!segs.length) return path
  const first = segs[0].toLowerCase()
  if (first === 'zh' || first === 'ja') return path
  if (first.length === 2 && /^[a-z]{2}$/i.test(first)) {
    const rest = segs.length > 1 ? '/' + segs.slice(1).join('/') : '/'
    return pathWithPreferredLocale(rest === '/' ? '/home' : rest)
  }
  return path
}

const router = createRouter({
  history: createWebHashHistory(),
  routes: [
    {
      path: '/admin/login',
      name: 'adminLogin',
      component: () => import('@/views/admin/Login')
    },
    {
      path: '/admin',
      component: () => import('@/components/admin/AdminLayout'),
      redirect: '/admin/books',
      children: [
        {
          path: 'books',
          name: 'adminBooks',
          component: () => import('@/views/admin/Books')
        },
        {
          path: 'book/new',
          name: 'adminBookNew',
          component: () => import('@/views/admin/BookNew')
        },
        {
          path: 'book/chapters',
          name: 'adminBookChapters',
          component: () => import('@/views/admin/BookChapters')
        },
        {
          path: 'recharge-min',
          name: 'adminRechargeMin',
          component: () => import('@/views/admin/RechargeMin')
        },
        {
          path: 'member-package',
          name: 'adminMemberPackage',
          component: () => import('@/views/admin/MemberPackage')
        },
        {
          path: 'user-points',
          name: 'adminUserPoints',
          component: () => import('@/views/admin/UserPoints')
        },
        {
          path: 'pay-offline',
          name: 'adminPayOffline',
          component: () => import('@/views/admin/PayOffline')
        },
        {
          path: 'consult',
          name: 'adminConsult',
          component: () => import('@/views/admin/Consult')
        },
        {
          path: 'book-categories',
          name: 'adminBookCategories',
          component: () => import('@/views/admin/BookCategories')
        },
        {
          path: 'home-recommend',
          name: 'adminHomeRecommend',
          component: () => import('@/views/admin/HomeRecommend')
        }
      ]
    },
    {
      path: '/:locale(zh|ja)',
      component: () => import('@/components/common/LocaleLayout'),
      children: localeChildRoutes
    }
  ]
})

router.beforeEach((to, from, next) => {
  if (to.path.startsWith('/admin') && to.path !== '/admin/login') {
    if (!getAdminToken()) {
      next({
        path: '/admin/login',
        query: { redirect: to.fullPath },
        replace: true
      })
      return
    }
    next()
    return
  }

  if (shouldSkipLocaleRedirect(to.path)) {
    next()
    return
  }

  let path = to.path
  if (path !== '/') {
    path = normalizeIncomingPath(path)
  }

  if (path === '/') {
    next({
      path: getRootRedirectPath(),
      query: to.query,
      hash: to.hash,
      replace: true
    })
    return
  }

  if (!/^\/(zh|ja)(\/|$)/.test(path)) {
    next({
      path: pathWithPreferredLocale(path),
      query: to.query,
      hash: to.hash,
      replace: true
    })
    return
  }

  if (path !== to.path) {
    next({
      path,
      query: to.query,
      hash: to.hash,
      replace: true
    })
    return
  }

  const loc = to.params.locale
  if (isSupportedLocale(loc)) {
    setActiveRouteLocale(loc)
    setStoredLocale(loc)
    setI18nLocale(loc)
  }

  next()
})

router.afterEach((to) => {
  window.scrollTo(0, 0)
  if (typeof document === 'undefined') return

  if (to.path.startsWith('/admin')) {
    document.documentElement.lang = 'zh-Hans'
    const adminSite = messages.zh.app.adminTitle
    document.title =
      to.path === '/admin/login'
        ? `${messages.zh.loginPage.titleShort} - ${adminSite}`
        : adminSite
    return
  }

  if (isSupportedLocale(to.params.locale)) {
    document.documentElement.lang = to.params.locale === 'ja' ? 'ja' : 'zh-Hans'
    document.title = i18n.global.t('app.siteTitle')
    return
  }

  document.documentElement.lang = 'zh-Hans'
  document.title = i18n.global.t('app.siteTitle')
})

export default router
